import { useContext, useEffect, useState } from 'react';
import SessionContext from '../contexts/SessionContext';
import { AskCarlThread } from '../types/askCarl';

/**
 * Read-only view of the user's Ask Carl thread index (persisted in
 * localStorage by useAskCarlChat) for surfacing recent conversations in the
 * command search palette. Skips blank "New conversation" threads and sorts
 * most-recently-updated first. Refreshes whenever `refreshKey` changes (e.g.
 * the palette opening) so it always reflects the latest threads.
 */
export function useAskCarlThreadPreviews(refreshKey: unknown, limit = 5): AskCarlThread[] {
    const session = useContext(SessionContext);
    const userId = session?.isAuthenticated ? session.userId : null;
    const [threads, setThreads] = useState<AskCarlThread[]>([]);

    useEffect(() => {
        if (!userId || typeof window === 'undefined') {
            setThreads([]);
            return;
        }
        try {
            const raw = window.localStorage.getItem(`ask-carl:threads:${userId}`);
            const parsed = raw ? (JSON.parse(raw) as AskCarlThread[]) : [];
            const cleaned = parsed
                .filter((thread) => thread.title?.trim() && thread.title !== 'New conversation')
                .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
                .slice(0, limit);
            setThreads(cleaned);
        } catch {
            setThreads([]);
        }
    }, [userId, limit, refreshKey]);

    return threads;
}

export default useAskCarlThreadPreviews;
